import React, { useEffect, useRef } from "react";

type EcoConfirmDialogProps = {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  /** `danger` pinta o botão de confirmar de vermelho (desativar, excluir). */
  tone?: "default" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Confirmação de ação que muda estado (desativar, reativar). Fecha no Esc e no
 * clique fora, exceto enquanto `busy` — a ação em andamento não é abandonada
 * pela metade.
 */
const EcoConfirmDialog: React.FC<EcoConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel,
  tone = "default",
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div className="ecoDialog__overlay" role="presentation" onClick={() => !busy && onCancel()}>
      <div
        className="ecoDialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="ecoDialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="ecoDialog-title" className="ecoDialog__title">
          {title}
        </h2>
        <div className="ecoDialog__text">{message}</div>
        <div className="ecoDialog__actions">
          <button ref={cancelRef} type="button" className="ecoBtn" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className={`ecoBtn ${tone === "danger" ? "ecoBtn--dangerSolid" : "ecoBtn--primary"}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Aguarde..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EcoConfirmDialog;
